import {
  createContext,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { CourseInfo, LecturePaidType, LectureType } from 'service/course';
import styles from './CourseDetails.module.scss';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import ViewLectureVideoPlayer from './ViewLectureVideoPlayer';
import CourseOverview from './CourseOverview';
import QuestionAnswerDialog from './QuestionsAnswerDialog';
import LecturesMenu from './LecturesMenu';
import CoursePaymentDialog from './CoursePaymentDialog';
import { useMediaQuery } from 'common/utils';
import ReviewSection from './ReviewsSection';
import { canWatchLectureVideo } from 'common/utils/course';
import Image from 'next/image';
import useGlobalStore from 'store/global';
import Button from 'components/common/Button';
interface CourseDetailsContextType {
  openQuestionAnswerDialog: boolean;
  setOpenQuestionAnswerDialog: (open: boolean) => void;
  courseInfo: CourseInfo;
  currentLecture: LectureType;
  setCurrentLecture: Dispatch<SetStateAction<LectureType>>;
  openCoursePaywall?: boolean;
  setOpenCoursePaywall?: (open: boolean) => void;
  refetchCourse?: () => void;
  viewdLectureIds: number[];
  setViewedLectureIds: Dispatch<SetStateAction<number[]>>;
}

export const CourseDetailsContext =
  createContext<CourseDetailsContextType | null>(null);

const CourseDetails = ({
  courseInfo,
  refetchCourse,
  currentLecture,
  setCurrentLecture,
}: {
  courseInfo: CourseInfo;
  refetchCourse: () => void;
  currentLecture: LectureType;
  setCurrentLecture: Dispatch<SetStateAction<LectureType>>;
}) => {
  const currentAuth = useGlobalStore((state) => state.currentAuth);
  const showLogin = useGlobalStore((state) => state.showLogin);
  const showMembershipPayDialogInput = useGlobalStore(
    (state) => state.showMembershipPayDialogInput
  );
  const showPaymentDialogInput = useGlobalStore(
    (state) => state.showPaymentDialogInput
  );

  const showTippingDialogInput = useGlobalStore(
    (state) => state.showTippingDialogInput
  );
  const isSmallScreen = useMediaQuery('(max-width: 1000px)');
  const [openCoursePaywall, setOpenCoursePaywall] = useState(false);
  const [viewdLectureIds, setViewedLectureIds] = useState<number[]>([]);
  const [openQuestionAnswerDialog, setOpenQuestionAnswerDialog] =
    useState(false);
  useEffect(() => {
    if (courseInfo?.id) {
      setViewedLectureIds([]);
    }
  }, [courseInfo?.id]);

  useEffect(() => {
    if (!openCoursePaywall) return;
    const showingStripeInDialog =
      (!!showPaymentDialogInput?.creator?.is_creator &&
        showPaymentDialogInput?.creator?.is_open_for_subscription) ||
      !!showTippingDialogInput ||
      !!showMembershipPayDialogInput?.open;
    if (showingStripeInDialog) {
      setOpenCoursePaywall(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showPaymentDialogInput,
    showTippingDialogInput,
    showMembershipPayDialogInput,
  ]);
  if (!courseInfo) return <></>;
  return (
    <CourseDetailsContext.Provider
      value={{
        openQuestionAnswerDialog,
        setOpenQuestionAnswerDialog,
        courseInfo,
        currentLecture,
        setCurrentLecture,
        openCoursePaywall,
        setOpenCoursePaywall,
        refetchCourse,
        viewdLectureIds,
        setViewedLectureIds,
      }}
    >
      <div className={styles.courseDetailWrapper}>
        <div className={styles.courseContentWrapper}>
          <div className={styles.lectureVideoWrapper}>
            {currentLecture ? (
              canWatchLectureVideo(courseInfo, currentLecture) &&
              currentAuth ? (
                <ViewLectureVideoPlayer lecture={currentLecture} />
              ) : (
                <div className={styles.lectureThumbnail}>
                  <Image
                    src={currentLecture.lecture_thumbnail_media?.url}
                    width={currentLecture.lecture_thumbnail_media?.width}
                    height={currentLecture.lecture_thumbnail_media?.height}
                    alt={currentLecture.title || ''}
                  />
                  {(!currentAuth ||
                    (currentLecture.type === LecturePaidType.Paid &&
                      !courseInfo.is_purchased)) && (
                    <div className={styles.registerBtnWrapper}>
                      <Button
                        text={
                          !currentAuth
                            ? '無料でログインする'
                            : 'コースを受講する'
                        }
                        onClick={() => {
                          if (!currentAuth) {
                            showLogin({});
                            return;
                          }
                          setOpenCoursePaywall(true);
                        }}
                      />
                    </div>
                  )}
                </div>
              )
            ) : (
              courseInfo.thumbnail_image_media && (
                <div className={styles.lectureThumbnail}>
                  <Image
                    src={courseInfo.thumbnail_image_media?.url}
                    width={courseInfo.thumbnail_image_media?.width}
                    height={courseInfo.thumbnail_image_media?.height}
                    alt={courseInfo.title || ''}
                  />
                </div>
              )
            )}
          </div>
          {isSmallScreen && <LecturesMenu />}
          <div className={styles.courseContent}>
            <Tabs
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              selectedTabClassName={styles.selected}
            >
              <TabList className={styles.courseContentTabs}>
                <Tab className={styles.tab} id="overview">
                  <div className={styles.tabContent}>概要</div>
                </Tab>
                <Tab className={styles.tab} id="reviews">
                  <div className={styles.tabContent}>レビュー</div>
                </Tab>
              </TabList>
              <TabPanel>
                <div className={styles.tabContentWrapper}>
                  <CourseOverview courseInfo={courseInfo} />
                </div>
              </TabPanel>
              <TabPanel>
                <div className={styles.tabContentWrapper}>
                  <ReviewSection />
                </div>
              </TabPanel>
            </Tabs>
          </div>
        </div>
        {!isSmallScreen && <LecturesMenu />}
        {openQuestionAnswerDialog && currentLecture && <QuestionAnswerDialog />}
        {currentAuth && openCoursePaywall && <CoursePaymentDialog />}
      </div>
    </CourseDetailsContext.Provider>
  );
};

export default CourseDetails;
